import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSiteSearchQuery, SiteSearchItem } from "data/resources/siteSearch";
import useAppStore from "shared/state/useAppState";
import "./SiteSearchPageStyles.scss";

interface ResultGroupProps {
  label: string;
  icon: string;
  items: SiteSearchItem[];
  onItemClick: (item: SiteSearchItem) => void;
}

const ResultGroup: React.FC<ResultGroupProps> = ({
  label,
  icon,
  items,
  onItemClick,
}) => {
  if (items.length === 0) return null;
  return (
    <section className="ss-group">
      <div className="ss-group-header">
        <i className={icon} />
        <span>{label}</span>
        <span className="ss-group-count">{items.length}</span>
      </div>
      <ul className="ss-group-list">
        {items.map((item) => (
          <li
            key={item.id}
            className="ss-item"
            onClick={() => onItemClick(item)}
          >
            <div className="ss-item-name">{item.name}</div>
            {item.summary && (
              <div className="ss-item-summary">{item.summary}</div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

const SiteSearchPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get("q") ?? "";
  const setIsLoading = useAppStore((app) => app.setIsLoading);

  const { data, isFetching } = useSiteSearchQuery(query);

  React.useEffect(() => {
    if (!isFetching) {
      setIsLoading(false);
    }
  }, [isFetching, setIsLoading]);

  const totalResults =
    (data?.categories.length ?? 0) +
    (data?.reports.length ?? 0) +
    (data?.links.length ?? 0) +
    (data?.downloads.length ?? 0) +
    (data?.pages.length ?? 0);

  const hasResults = totalResults > 0;

  const handleCategory = (item: SiteSearchItem) => {
    navigate(`/category/${encodeURIComponent(item.name)}`);
  };

  const handleReport = (item: SiteSearchItem) => {
    if (item.categoryId) {
      navigate(`/category/${item.categoryId}/report/${item.id}`);
    } else {
      navigate(`/category/0/report/${item.id}`);
    }
  };

  const handleLink = (item: SiteSearchItem) => {
    if (item.url) {
      window.open(item.url, "_blank", "noopener,noreferrer");
    }
  };

  const handleDownload = (item: SiteSearchItem) => {
    if (item.url) {
      window.open(item.url, "_blank", "noopener,noreferrer");
    }
  };

  const handlePage = (item: SiteSearchItem) => {
    if (item.url) {
      if (item.url.startsWith("/")) {
        navigate(item.url);
      } else {
        window.open(item.url, "_blank", "noopener,noreferrer");
      }
    }
  };

  return (
    <div className={`ss-page${hasResults ? " ss-has-results" : ""}`}>
      {hasResults ? (
        <>
          <div className="ss-page-header">
            <div className="ss-page-title">
              <i className="fa-solid fa-magnifying-glass" />
              <span>Search Results</span>
            </div>
            <div className="ss-page-meta">
              {isFetching ? (
                <span className="ss-searching">Searching…</span>
              ) : (
                <span>
                  {totalResults} result{totalResults !== 1 ? "s" : ""} for{" "}
                  <strong>"{query}"</strong>
                </span>
              )}
            </div>
          </div>

          <div className="ss-results">
            <ResultGroup
              label="Categories"
              icon="fa-solid fa-folder-tree"
              items={data?.categories ?? []}
              onItemClick={handleCategory}
            />
            <ResultGroup
              label="Reports"
              icon="fa-solid fa-chart-column"
              items={data?.reports ?? []}
              onItemClick={handleReport}
            />
            <ResultGroup
              label="Links"
              icon="fa-solid fa-link"
              items={data?.links ?? []}
              onItemClick={handleLink}
            />
            <ResultGroup
              label="Downloads"
              icon="fa-solid fa-file-arrow-down"
              items={data?.downloads ?? []}
              onItemClick={handleDownload}
            />
            <ResultGroup
              label="Pages"
              icon="fa-solid fa-file-lines"
              items={data?.pages ?? []}
              onItemClick={handlePage}
            />
          </div>
        </>
      ) : (
        <div className="ss-empty-state-wrapper">
          <div>
            <div className="ss-page-header">
              <div className="ss-page-title">
                <i className="fa-solid fa-magnifying-glass" />
                <span>Search Results</span>
              </div>
              {query.trim().length >= 2 && !isFetching && (
                <div className="ss-page-meta">
                  No results for <strong>"{query}"</strong>
                </div>
              )}
              {query.trim().length < 2 && (
                <div className="ss-page-meta">
                  Enter at least 2 characters to search.
                </div>
              )}
            </div>

            {!isFetching && !hasResults && query.trim().length >= 2 && (
              <div className="ss-empty">
                <i className="fa-solid fa-circle-exclamation" />
                <p>
                  No results found for <strong>"{query}"</strong>.
                </p>
                <p className="ss-empty-hint">
                  Try different keywords or check your spelling.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteSearchPage;
